import type {
  WhatsAppTemplate,
  WhatsAppTemplateButton,
  WhatsAppTemplateHeader,
  WhatsAppTemplateStatus,
} from '@/types/whatsapp-template';

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function asNumber(value: unknown, fallback = 0): number {
  return typeof value === 'number' && !Number.isNaN(value) ? value : fallback;
}

function asBoolean(value: unknown): boolean {
  return value === true;
}

function normalizeStatus(value: unknown): WhatsAppTemplateStatus {
  const raw = asString(value).toLowerCase();
  if (raw === 'approved') return 'approved';
  if (raw === 'rejected') return 'rejected';
  return 'pending';
}

function normalizeHeader(raw: unknown): WhatsAppTemplateHeader | null {
  if (!raw) return null;
  const record = asRecord(raw);
  if (!record) return null;
  return record as WhatsAppTemplateHeader;
}

function normalizeButton(raw: unknown): WhatsAppTemplateButton | null {
  const record = asRecord(raw);
  if (!record) return null;
  const text = asString(record.text);
  if (!text) return null;
  return {
    ...record,
    type: asString(record.type) || undefined,
    text,
    url: asString(record.url) || undefined,
    phoneNumber:
      asString(record.phoneNumber ?? record.phone_number) || undefined,
  };
}

export function normalizeWhatsAppTemplate(
  raw: unknown
): WhatsAppTemplate | null {
  const record = asRecord(raw);
  if (!record) return null;

  const id = record.id;
  const title = asString(record.title);
  const name = asString(record.name);
  if (id == null || (!title && !name)) return null;

  const buttonsRaw = Array.isArray(record.buttons) ? record.buttons : [];
  const buttons = buttonsRaw
    .map(normalizeButton)
    .filter((button): button is WhatsAppTemplateButton => button !== null);

  const variables = Array.isArray(record.variables)
    ? record.variables.filter(
        (item): item is string => typeof item === 'string'
      )
    : [];

  return {
    id: typeof id === 'number' ? id : parseInt(String(id), 10),
    businessId:
      typeof record.businessId === 'number' ? record.businessId : null,
    metaTemplateId: asString(record.metaTemplateId) || null,
    name: name || title.toLowerCase().replace(/\s+/g, '_'),
    title: title || name,
    category: asString(record.category),
    language: asString(record.language, 'en'),
    header: normalizeHeader(record.header),
    body: asString(record.body),
    footer: asString(record.footer) || null,
    buttons,
    variables,
    status: normalizeStatus(record.status),
    internalNote: asString(record.internalNote) || null,
    isPreApproved: asBoolean(record.isPreApproved),
    isAiCompatible: asBoolean(record.isAiCompatible),
    sentCount: asNumber(record.sentCount),
    replyRate: asNumber(record.replyRate),
    deliveryRate: asNumber(record.deliveryRate),
    rejectionReason: asString(record.rejectionReason) || null,
    createdAt: asString(record.createdAt),
    updatedAt: asString(record.updatedAt),
  };
}

export function normalizeWhatsAppTemplates(raw: unknown): WhatsAppTemplate[] {
  if (Array.isArray(raw)) {
    return raw
      .map(normalizeWhatsAppTemplate)
      .filter((item): item is WhatsAppTemplate => item !== null);
  }

  const record = asRecord(raw);
  if (!record) return [];

  if (Array.isArray(record.data)) {
    return normalizeWhatsAppTemplates(record.data);
  }

  return [];
}
